import React, { useState, useEffect } from 'react';
import { ALL_COUNTRIES, TOP_COUNTRIES, CountryDialCode, toLocalDisplay, validatePhoneNumberForCountry } from '../../utils/formatters';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onCountryCodeChange?: (dialCode: string) => void;
  onBlur?: () => void;
  error?: string;
  setError?: (error: string) => void;
  defaultDialCode?: string;
  required?: boolean;
  className?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onCountryCodeChange,
  onBlur,
  error,
  setError,
  defaultDialCode = '+61',
  required = false,
  className = ''
}) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState(() => {
    const country = ALL_COUNTRIES.find(c => c.dialCode === defaultDialCode);
    return country ? country.code : 'AU';
  });
  const [placeholder, setPlaceholder] = useState('0412 345 678');

  // Derive dial code from selected country code
  const selectedCountry: CountryDialCode =
    ALL_COUNTRIES.find(c => c.code === selectedCountryCode) || TOP_COUNTRIES[0];

  useEffect(() => {
    const country = ALL_COUNTRIES.find(c => c.dialCode === defaultDialCode);
    if (country) {
      setSelectedCountryCode(country.code);
      setPlaceholder(country.placeholder);
    }
  }, [defaultDialCode]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    setSelectedCountryCode(countryCode);
    const country = ALL_COUNTRIES.find(c => c.code === countryCode);
    if (country) {
      setPlaceholder(country.placeholder);
      if (onCountryCodeChange) {
        onCountryCodeChange(country.dialCode);
      }
    }
    if (setError) {
      setError('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    onChange(digitsOnly);
    if (setError) {
      setError('');
    }
  };

  const handleBlur = () => {
    // Rewrite what the user typed into this country's local format, so a pasted
    // "+61404211211" shows (and is sent) as 0404211211
    const normalised = toLocalDisplay(value, selectedCountry.dialCode);
    if (normalised !== value) {
      onChange(normalised);
    }

    if (setError) {
      const validationError = validatePhoneNumberForCountry(normalised, selectedCountry.dialCode);
      setError(validationError);
    }
    if (onBlur) onBlur();
  };

  const topCountryCodes = new Set(TOP_COUNTRIES.map(c => c.code));
  const otherCountries = ALL_COUNTRIES.filter(c => !topCountryCodes.has(c.code));

  return (
    <div>
      <div className={`flex border ${error ? 'border-red-500' : 'border-gray-300'} rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-racing-blue focus-within:border-racing-blue ${className}`}>
        <div className="relative flex-shrink-0">
          <select
            value={selectedCountryCode}
            onChange={handleCountryChange}
            className="appearance-none h-full bg-gray-50 border-r border-gray-300 pl-2 pr-6 py-2 text-sm text-gray-700 focus:outline-none cursor-pointer"
            aria-label="Country dial code"
          >
            <optgroup>
              {TOP_COUNTRIES.map(c => (
                <option key={`top-${c.code}`} value={c.code}>
                  {c.flag} {c.code} {c.dialCode}
                </option>
              ))}
            </optgroup>
            <optgroup>
              {otherCountries.map(c => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.code} {c.dialCode}
                </option>
              ))}
            </optgroup>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center">
            <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9\s]*"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-white"
          placeholder={placeholder}
          required={required}
        />
      </div>
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
      <p className="text-xs text-gray-500 mt-1">
        Enter your mobile number (e.g., {selectedCountry.placeholder})
      </p>
    </div>
  );
};

export default PhoneInput;
